import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Box,
  Avatar,
  Divider,
  Badge,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Article as ArticleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  ContentCopy as ContentCopyIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';
import { getPressReleases, deletePressRelease, getPressRelease, PressReleaseResponse } from '../../services/prApi';

interface PRItem {
  id: string;
  title: string;
  status: 'draft' | 'submitted' | 'published' | 'rejected' | 'verified' | 'distributed';
  createdAt: string;
  updatedAt: string;
  category: string;
  industry: string;
  channels: number;
  views: number;
  engagement: number;
  author: string;
  tags: string[];
  content?: string;
  contactInfo?: {
    name: string;
    email: string;
    phone?: string;
    company: string;
  };
  originalStatus?: string; // Store original status from backend
  targetAudience?: string[];
  distributionChannels?: string[];
  verificationStatus?: string;
  aiAnalysis?: any;
}

const PRLibrary: React.FC = () => {
  const navigate = useNavigate();
  const [prs, setPRs] = useState<PRItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPR, setSelectedPR] = useState<PRItem | null>(null);
  const [fullPRDetails, setFullPRDetails] = useState<PressReleaseResponse | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch press releases from backend
  // TODO: Backend optimization suggestions:
  // 1. Add pagination support (limit, skip) to reduce initial load
  // 2. Add field selection to exclude large fields like 'content' unless needed
  // 3. Add caching headers for better performance
  // 4. Consider adding a lightweight summary endpoint for list views
  useEffect(() => {
    const fetchPRs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getPressReleases();
        
        // Transform backend data to frontend format - optimized
        // Don't store full content unless needed (saves memory and processing)
        const transformedPRs: PRItem[] = response.map((pr: PressReleaseResponse) => {
          // Extract company name from contactInfo or author info
          const company = pr.contactInfo?.company || pr.author?.company || 'Unknown';
          const authorName = pr.author?.fullName 
            ? pr.author.fullName.trim() 
            : pr.contactInfo?.name || 'Unknown';
          
          // Extract tags from targetAudience or use default tags
          const tags = pr.targetAudience?.slice(0, 3) || ['General'];
          
          // Determine category based on content or default
          const category = pr.aiAnalysis?.category || 'General News';
          
          // Determine industry from contactInfo company or default
          const industry = pr.contactInfo?.company || 'General';
          
          // Calculate real metrics from analytics data
          const channels = pr.distributionChannels?.length || 0;
          
          // Calculate total views from analytics publications (using impressions as views)
          let views = 0;
          if (pr.analytics?.publications && Array.isArray(pr.analytics.publications)) {
            views = pr.analytics.publications.reduce((sum: number, pub: any) => {
              // Use impressions as views (impressions = page views/views)
              return sum + (pub.impressions || 0);
            }, 0);
          }
          // Fallback to analytics totalImpressions if available
          if (views === 0 && pr.analytics?.totalImpressions) {
            views = pr.analytics.totalImpressions;
          }
          
          // Calculate engagement as CTR (clicks/views) percentage
          let engagement = 0;
          if (pr.analytics?.publications && Array.isArray(pr.analytics.publications)) {
            const totalClicks = pr.analytics.publications.reduce((sum: number, pub: any) => {
              return sum + (pub.clicks || 0);
            }, 0);
            if (views > 0 && totalClicks > 0) {
              // Calculate CTR as percentage (clicks / views * 100)
              engagement = Math.round((totalClicks / views) * 100 * 10) / 10; // 1 decimal place
            }
          }
          // Fallback: use totalClicks from analytics
          if (engagement === 0 && pr.analytics?.totalClicks && views > 0) {
            engagement = Math.round((pr.analytics.totalClicks / views) * 100 * 10) / 10;
          }
          
          // Map status to match frontend expected values - keep original status for filtering
          let status: 'draft' | 'submitted' | 'published' | 'rejected' | 'verified' | 'distributed';
          const originalStatus = pr.status;
          // Preserve all statuses as-is for proper filtering
          if (pr.status === 'published' || pr.status === 'distributed' || pr.status === 'verified' || 
              pr.status === 'draft' || pr.status === 'submitted' || pr.status === 'rejected') {
            status = pr.status as any;
          } else {
            // Default to draft for unknown statuses
            status = 'draft';
          }
          
          return {
            id: pr._id,
            title: pr.title,
            status,
            createdAt: pr.createdAt,
            updatedAt: pr.updatedAt,
            category,
            industry,
            channels,
            views,
            engagement,
            author: authorName,
            tags,
            // Don't store full content - fetch it when needed (saves memory)
            content: undefined,
            contactInfo: pr.contactInfo,
            originalStatus,
            targetAudience: pr.targetAudience,
            distributionChannels: pr.distributionChannels,
            verificationStatus: pr.verificationStatus,
            aiAnalysis: pr.aiAnalysis
          };
        });
        
        setPRs(transformedPRs);
      } catch (err: any) {
        console.error('Error fetching press releases:', err);
        setError(err.message || 'Failed to load press releases');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPRs();
  }, []);

  // Memoized filtered PRs - optimized filtering
  const filteredPRs = useMemo(() => {
    let filtered = prs;

    // Optimize search - only search title and tags (content removed for performance)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(pr => 
        pr.title.toLowerCase().includes(searchLower) ||
        pr.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (statusFilter !== 'all') {
      // Filter by status - check both current status and original status
      filtered = filtered.filter(pr => {
        if (pr.status === statusFilter) return true;
        // Also check originalStatus for cases where status was transformed
        if (pr.originalStatus === statusFilter) return true;
        return false;
      });
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(pr => pr.category === categoryFilter);
    }

    return filtered;
  }, [searchTerm, statusFilter, categoryFilter, prs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, categoryFilter]);

  // Memoized statistics - calculate once and reuse
  const stats = useMemo(() => {
    const totalPRs = prs.length;
    const published = prs.filter(pr => pr.status === 'published').length;
    const distributed = prs.filter(pr => pr.status === 'distributed').length;
    const drafts = prs.filter(pr => pr.status === 'draft').length;
    const totalViews = prs.reduce((sum, pr) => sum + (pr.views || 0), 0);
    const totalEngagement = prs.length > 0 
      ? Math.round(prs.reduce((sum, pr) => sum + (pr.engagement || 0), 0) / prs.length)
      : 0;

    return { totalPRs, published, distributed, drafts, totalViews, totalEngagement };
  }, [prs]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'published': return <CheckCircleIcon style={{ color: '#4CAF50' }} />;
      case 'distributed': return <PublicIcon style={{ color: '#4CAF50' }} />;
      case 'verified': return <CheckCircleIcon style={{ color: '#00BCD4' }} />;
      case 'submitted': return <PendingIcon style={{ color: '#FF9800' }} />;
      case 'draft': return <EditIcon style={{ color: '#2196F3' }} />;
      case 'rejected': return <ErrorIcon style={{ color: '#F44336' }} />;
      default: return <PendingIcon style={{ color: '#9E9E9E' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return '#4CAF50';
      case 'distributed': return '#4CAF50';
      case 'verified': return '#00BCD4';
      case 'submitted': return '#FF9800';
      case 'draft': return '#2196F3';
      case 'rejected': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, pr: PRItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedPR(pr);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPR(null);
  };

  const handleViewPR = async () => {
    if (selectedPR) {
      setViewDialogOpen(true);
      setLoadingDetails(true);
      setFullPRDetails(null);
      try {
        const fullDetails = await getPressRelease(selectedPR.id);
        setFullPRDetails(fullDetails);
      } catch (err: any) {
        console.error('Error fetching PR details:', err);
        setError(err.message || 'Failed to load PR details');
      } finally {
        setLoadingDetails(false);
      }
    }
    handleMenuClose();
  };

  const handleEditPR = async () => {
    if (selectedPR) {
      try {
        // Fetch full PR details to determine the correct step
        const fullDetails = await getPressRelease(selectedPR.id);
        
        // Determine which step to resume from based on PR status and data
        let step = 1; // Default to step 1 (Upload & Compose)
        
        if (selectedPR.status === 'draft') {
          // If draft, check if there's content - if yes, go to step 2 (AI Analysis)
          step = (fullDetails.content && fullDetails.content.trim()) ? 2 : 1;
        } else if (selectedPR.status === 'submitted' || selectedPR.status === 'verified') {
          // If submitted or verified, check verification status
          if (fullDetails.verificationStatus === 'verified') {
            step = 4; // Review & Submit
          } else {
            step = 3; // Verification
          }
        } else if (selectedPR.status === 'published' || selectedPR.status === 'distributed') {
          // If published, can still edit but start from step 2 (AI Analysis)
          step = 2;
        } else if (selectedPR.status === 'rejected') {
          // If rejected, go back to step 2 to fix issues
          step = 2;
        }
        
        // Store PR ID and step in localStorage for the PR submission interface to pick up
        localStorage.setItem('prId', selectedPR.id);
        localStorage.setItem('prCurrentStep', String(step));
        localStorage.setItem('prContent', fullDetails.content || '');
        
        if (fullDetails.verificationStatus === 'verified') {
          localStorage.setItem('prVerificationComplete', 'true');
        } else {
          localStorage.setItem('prVerificationComplete', 'false');
        }
        
        // Store that we're editing an existing PR
        localStorage.setItem('isEditingPR', 'true');
        
        // Navigate to create-pr page
        navigate('/create-pr');
      } catch (err: any) {
        console.error('Error fetching PR details for editing:', err);
        setError(err.message || 'Failed to load PR details for editing');
        // Still try to navigate with basic info
        localStorage.setItem('prId', selectedPR.id);
        localStorage.setItem('prCurrentStep', '1');
        localStorage.setItem('isEditingPR', 'true');
        navigate('/create-pr');
      }
    }
    handleMenuClose();
  };


  const handleDeletePR = useCallback(async () => {
    if (selectedPR) {
      try {
        await deletePressRelease(selectedPR.id);
        // Remove from local state - filteredPRs will update automatically via useMemo
        setPRs(prevPRs => prevPRs.filter(pr => pr.id !== selectedPR.id));
      } catch (err: any) {
        console.error('Error deleting press release:', err);
        setError(err.message || 'Failed to delete press release');
      }
    }
    handleMenuClose();
  }, [selectedPR]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ArticleIcon style={{ fontSize: '32px' }} />
          PR Library
        </Typography>
        <Typography variant="body1" style={{ color: '#666', marginBottom: '16px' }}>
          Manage and track all your press releases
        </Typography>
        
        {/* Status Legend */}
        <Box style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#0927eb', marginBottom: '12px' }}>
            What the Status Means
          </Typography>
          <Box style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PublicIcon style={{ fontSize: '20px', color: '#4CAF50' }} />
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                  Distributed
                </Typography>
                <Typography variant="caption" style={{ color: '#666', fontSize: '0.75rem' }}>
                  PR sent to media channels/publishers (24-48 hrs to publish)
                </Typography>
              </Box>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircleIcon style={{ fontSize: '20px', color: '#4CAF50' }} />
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                  Published
                </Typography>
                <Typography variant="caption" style={{ color: '#666', fontSize: '0.75rem' }}>
                  PR is live and published on media channels
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" style={{ marginBottom: '24px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </div>
      ) : (
        <>

      {/* Stats Cards */}
      <Grid container spacing={3} style={{ marginBottom: '32px' }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                    {stats.totalPRs}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Total PRs
                  </Typography>
                </div>
                <ArticleIcon style={{ fontSize: '40px', color: '#0927eb', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                    {stats.published}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Published
                  </Typography>
                </div>
                <CheckCircleIcon style={{ fontSize: '40px', color: '#4CAF50', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                    {stats.distributed}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Distributed PR
                  </Typography>
                </div>
                <PublicIcon style={{ fontSize: '40px', color: '#4CAF50', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196F3' }}>
                    {stats.drafts}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Drafts
                  </Typography>
                </div>
                <EditIcon style={{ fontSize: '40px', color: '#2196F3', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper style={{ padding: '24px', marginBottom: '24px', borderRadius: '12px' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search PRs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="distributed">Distributed (Sent to media channels)</MenuItem>
                <MenuItem value="published">Published (Live on media channels)</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="Product Launch">Product Launch</MenuItem>
                <MenuItem value="Business News">Business News</MenuItem>
                <MenuItem value="Funding">Funding</MenuItem>
                <MenuItem value="Partnership">Partnership</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              style={{
                backgroundColor: '#000000',
                color: 'white',
                fontWeight: 'bold'
              }}
              onClick={() => {
                // Clear any saved PR state for new PR
                localStorage.removeItem('prId');
                localStorage.removeItem('prCurrentStep');
                localStorage.removeItem('prVerificationComplete');
                navigate('/create-pr');
              }}
            >
              New PR
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* PR Table */}
      <TableContainer component={Paper} style={{ borderRadius: '12px' }}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell style={{ fontWeight: 'bold' }}>Title</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Category</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Industry</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Views</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Engagement</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Author</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Updated</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPRs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" style={{ padding: '40px' }}>
                  <Typography variant="body1" style={{ color: '#999' }}>
                    {loading ? 'Loading...' : 'No press releases found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPRs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((pr) => (
                <TableRow key={pr.id} hover>
                  <TableCell>
                    <div>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        {pr.title}
                      </Typography>
                      <div style={{ marginTop: '4px' }}>
                        {pr.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            style={{ marginRight: '4px', marginBottom: '2px' }}
                          />
                        ))}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        pr.status === 'distributed' 
                          ? 'Distributed: PR has been sent to media channels/publishers. Publishing typically takes 24-48 hours.'
                          : pr.status === 'published'
                          ? 'Published: PR is now live on media channels.'
                          : ''
                      }
                      arrow
                    >
                      <Chip
                        icon={getStatusIcon(pr.status)}
                        label={pr.status.charAt(0).toUpperCase() + pr.status.slice(1)}
                        style={{
                          backgroundColor: getStatusColor(pr.status),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{pr.category}</TableCell>
                  <TableCell>{pr.industry}</TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <VisibilityIcon style={{ fontSize: '16px', color: '#666' }} />
                      {pr.views.toLocaleString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <TrendingUpIcon style={{ fontSize: '16px', color: '#666' }} />
                      {pr.engagement}%
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Avatar style={{ width: '24px', height: '24px', fontSize: '12px' }}>
                        {pr.author.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      {pr.author}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CalendarIcon style={{ fontSize: '16px', color: '#666' }} />
                      {new Date(pr.updatedAt).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleMenuClick(e, pr)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPRs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewPR}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditPR}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeletePR} style={{ color: '#F44336' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" style={{ color: '#F44336' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* View PR Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setFullPRDetails(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" style={{ fontWeight: 'bold' }}>
            {selectedPR?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress />
            </div>
          ) : (selectedPR || fullPRDetails) ? (
            <div style={{ marginTop: '16px' }}>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Divider style={{ marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                    Basic Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                    Status
                  </Typography>
                  <Chip
                    icon={getStatusIcon(selectedPR?.status || fullPRDetails?.status || 'draft')}
                    label={(selectedPR?.status || fullPRDetails?.status || 'draft').charAt(0).toUpperCase() + (selectedPR?.status || fullPRDetails?.status || 'draft').slice(1)}
                    style={{
                      backgroundColor: getStatusColor(selectedPR?.status || fullPRDetails?.status || 'draft'),
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                  {fullPRDetails?.verificationStatus && (
                    <Typography variant="caption" style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                      Verification: {fullPRDetails.verificationStatus}
                    </Typography>
                  )}
                  {(selectedPR?.status === 'distributed' || fullPRDetails?.status === 'distributed') && (
                    <Box style={{ marginTop: '8px', padding: '8px', backgroundColor: '#FFF3E0', borderRadius: '4px', border: '1px solid #FF9800' }}>
                      <Typography variant="caption" style={{ display: 'flex', alignItems: 'center', color: '#E65100', fontWeight: 600 }}>
                        <ScheduleIcon style={{ fontSize: '14px', marginRight: '4px' }} />
                        Distributed to Media Channels
                      </Typography>
                      <Typography variant="caption" style={{ display: 'block', marginTop: '4px', color: '#E65100', fontSize: '0.7rem' }}>
                        Your PR has been sent to publishers. Publishing typically takes 24-48 hours after distribution.
                      </Typography>
                    </Box>
                  )}
                  {(selectedPR?.status === 'published' || fullPRDetails?.status === 'published') && (
                    <Box style={{ marginTop: '8px', padding: '8px', backgroundColor: '#E8F5E9', borderRadius: '4px', border: '1px solid #4CAF50' }}>
                      <Typography variant="caption" style={{ display: 'flex', alignItems: 'center', color: '#2E7D32', fontWeight: 600 }}>
                        <CheckCircleIcon style={{ fontSize: '14px', marginRight: '4px' }} />
                        Published on Media Channels
                      </Typography>
                      <Typography variant="caption" style={{ display: 'block', marginTop: '4px', color: '#2E7D32', fontSize: '0.7rem' }}>
                        Your PR is now live and published on media channels.
                      </Typography>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                    Category
                  </Typography>
                  <Typography>{fullPRDetails?.aiAnalysis?.category || selectedPR?.category || 'N/A'}</Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                    Industry
                  </Typography>
                  <Typography>{fullPRDetails?.contactInfo?.company || selectedPR?.industry || 'N/A'}</Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                    Author
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                      {(fullPRDetails?.author?.fullName || selectedPR?.author || 'U').split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Typography>{fullPRDetails?.author?.fullName || selectedPR?.author || 'Unknown'}</Typography>
                  </div>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                    Created Date
                  </Typography>
                  <Typography>{new Date(fullPRDetails?.createdAt || selectedPR?.createdAt || new Date()).toLocaleString()}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                    Last Updated
                  </Typography>
                  <Typography>{new Date(fullPRDetails?.updatedAt || selectedPR?.updatedAt || new Date()).toLocaleString()}</Typography>
                </Grid>

                {/* Contact Information */}
                {(fullPRDetails?.contactInfo || selectedPR?.contactInfo) && (
                  <>
                    <Grid item xs={12}>
                      <Divider style={{ marginTop: '8px', marginBottom: '16px' }} />
                      <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                        Contact Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                        Contact Name
                      </Typography>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <PersonIcon style={{ fontSize: '16px', color: '#666' }} />
                        <Typography>{fullPRDetails?.contactInfo?.name || selectedPR?.contactInfo?.name || 'N/A'}</Typography>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                        Email
                      </Typography>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <EmailIcon style={{ fontSize: '16px', color: '#666' }} />
                        <Typography>{fullPRDetails?.contactInfo?.email || selectedPR?.contactInfo?.email || 'N/A'}</Typography>
                      </div>
                    </Grid>
                    {(fullPRDetails?.contactInfo?.phone || selectedPR?.contactInfo?.phone) && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                          Phone
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <PhoneIcon style={{ fontSize: '16px', color: '#666' }} />
                          <Typography>{fullPRDetails?.contactInfo?.phone || selectedPR?.contactInfo?.phone}</Typography>
                        </div>
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                        Company
                      </Typography>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <BusinessIcon style={{ fontSize: '16px', color: '#666' }} />
                        <Typography>{fullPRDetails?.contactInfo?.company || selectedPR?.contactInfo?.company || 'N/A'}</Typography>
                      </div>
                    </Grid>
                  </>
                )}

                {/* Tags and Target Audience */}
                <Grid item xs={12}>
                  <Divider style={{ marginTop: '8px', marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                    Tags & Audience
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                    Tags
                  </Typography>
                  <div>
                    {(() => {
                      const tags = fullPRDetails?.targetAudience || selectedPR?.tags || [];
                      return tags.length > 0 ? (
                        tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            style={{ marginRight: '8px', marginBottom: '4px' }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" style={{ color: '#999' }}>No tags</Typography>
                      );
                    })()}
                  </div>
                </Grid>
                {fullPRDetails?.targetAudience && fullPRDetails.targetAudience.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#666' }}>
                      Target Audience
                    </Typography>
                    <div>
                      {fullPRDetails.targetAudience.map((audience, index) => (
                        <Chip
                          key={index}
                          label={audience}
                          size="small"
                          variant="outlined"
                          style={{ marginRight: '8px', marginBottom: '4px' }}
                        />
                      ))}
                    </div>
                  </Grid>
                )}

                {/* Distribution Channels */}
                {fullPRDetails?.distributionChannels && fullPRDetails.distributionChannels.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Divider style={{ marginTop: '8px', marginBottom: '16px' }} />
                      <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                        Distribution Channels
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <div>
                        {fullPRDetails.distributionChannels.map((channel, index) => (
                          <Chip
                            key={index}
                            label={channel}
                            size="small"
                            icon={<PublicIcon />}
                            style={{ marginRight: '8px', marginBottom: '4px' }}
                          />
                        ))}
                      </div>
                    </Grid>
                  </>
                )}

                {/* Content Preview */}
                {(fullPRDetails?.content || selectedPR?.content) && (
                  <>
                    <Grid item xs={12}>
                      <Divider style={{ marginTop: '8px', marginBottom: '16px' }} />
                      <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                        Content Preview
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper style={{ padding: '16px', backgroundColor: '#f9f9f9', maxHeight: '300px', overflow: 'auto' }}>
                        <Typography 
                          variant="body2" 
                          style={{ 
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.6'
                          }}
                        >
                          {(() => {
                            const content = fullPRDetails?.content || selectedPR?.content || '';
                            return content.length > 1000 
                              ? `${content.substring(0, 1000)}...` 
                              : content;
                          })()}
                        </Typography>
                      </Paper>
                    </Grid>
                  </>
                )}

                {/* AI Analysis */}
                {fullPRDetails?.aiAnalysis && (
                  <>
                    <Grid item xs={12}>
                      <Divider style={{ marginTop: '8px', marginBottom: '16px' }} />
                      <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                        AI Analysis
                      </Typography>
                    </Grid>
                    {fullPRDetails.aiAnalysis.structureScore !== undefined && (
                      <Grid item xs={12} md={4}>
                        <Paper style={{ padding: '16px', textAlign: 'center' }}>
                          <Typography variant="h6" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                            {fullPRDetails.aiAnalysis.structureScore}%
                          </Typography>
                          <Typography variant="body2" style={{ color: '#666' }}>
                            Structure Score
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    {fullPRDetails.aiAnalysis.readabilityScore !== undefined && (
                      <Grid item xs={12} md={4}>
                        <Paper style={{ padding: '16px', textAlign: 'center' }}>
                          <Typography variant="h6" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                            {fullPRDetails.aiAnalysis.readabilityScore}%
                          </Typography>
                          <Typography variant="body2" style={{ color: '#666' }}>
                            Readability Score
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    {fullPRDetails.aiAnalysis.seoScore !== undefined && (
                      <Grid item xs={12} md={4}>
                        <Paper style={{ padding: '16px', textAlign: 'center' }}>
                          <Typography variant="h6" style={{ fontWeight: 'bold', color: '#FF9800' }}>
                            {fullPRDetails.aiAnalysis.seoScore}%
                          </Typography>
                          <Typography variant="body2" style={{ color: '#666' }}>
                            SEO Score
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </>
                )}

                {/* Performance Metrics */}
                <Grid item xs={12}>
                  <Divider style={{ marginTop: '8px', marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                    Performance Metrics
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Paper style={{ padding: '16px', textAlign: 'center' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                      {fullPRDetails?.distributionChannels?.length || selectedPR?.channels || 0}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      Channels
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper style={{ padding: '16px', textAlign: 'center' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                      {(fullPRDetails?.aiAnalysis?.views || selectedPR?.views || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      Views
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper style={{ padding: '16px', textAlign: 'center' }}>
                    <Typography variant="h6" style={{ fontWeight: 'bold', color: '#FF9800' }}>
                      {fullPRDetails?.aiAnalysis?.engagement || selectedPR?.engagement || 0}%
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666' }}>
                      Engagement
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <Typography variant="body1" style={{ color: '#999' }}>
                No details available
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setViewDialogOpen(false);
            setFullPRDetails(null);
          }}>
            Close
          </Button>
          <Button
            variant="contained"
            style={{
              backgroundColor: '#000000',
              color: 'white'
            }}
            onClick={() => {
              const prToEdit = selectedPR;
              setViewDialogOpen(false);
              setFullPRDetails(null);
              if (prToEdit) {
                // Determine which step to resume from based on PR status
                let step = 1; // Default to step 1 (Upload & Compose)
                
                if (prToEdit.status === 'draft') {
                  // If draft, check if there's content - if yes, go to step 2 (AI Analysis)
                  step = prToEdit.content ? 2 : 1;
                } else if (prToEdit.status === 'submitted' || prToEdit.status === 'verified') {
                  // If submitted or verified, go to step 3 (Verification) or step 4 (Review & Submit)
                  step = prToEdit.verificationStatus === 'verified' ? 4 : 3;
                } else if (prToEdit.status === 'published' || prToEdit.status === 'distributed') {
                  // If published, can still edit but start from step 2
                  step = 2;
                }
                
                // Store PR ID and step in localStorage for the PR submission interface to pick up
                localStorage.setItem('prId', prToEdit.id);
                localStorage.setItem('prCurrentStep', String(step));
                if (prToEdit.verificationStatus === 'verified') {
                  localStorage.setItem('prVerificationComplete', 'true');
                } else {
                  localStorage.setItem('prVerificationComplete', 'false');
                }
                
                // Navigate to create-pr page
                navigate('/create-pr');
              }
            }}
          >
            Edit PR
          </Button>
        </DialogActions>
      </Dialog>

      </>
      )}
    </Container>
  );
};

export default PRLibrary;